import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, ExternalLink } from "lucide-react";
import { IMPACT_CASES } from "../data/impactCases";
import StatCountUp from "../components/StatCountUp";

const reveal = { initial: { opacity: 0, y: 40 }, whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" }, transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1] } };

function StatsBlock({ items, size = "hero" }) {
  const bigClass = size === "hero"
    ? "font-display text-5xl md:text-7xl text-[var(--gb-gold)] leading-[0.95]"
    : "font-display text-5xl md:text-6xl text-white leading-[0.95]";
  return (
    <div className={`grid gap-8 ${items.length > 2 ? "md:grid-cols-2" : "md:grid-cols-2"}`}>
      {items.map((s, i) => (
        <div key={i}>
          <p className={bigClass}><StatCountUp value={s.big} /></p>
          <p className="kbd text-white/50 mt-3">{s.label}</p>
        </div>
      ))}
    </div>
  );
}

function FlowLine({ steps }) {
  return (
    <div className="mt-10 flex flex-col md:flex-row md:items-center gap-3 md:gap-2 flex-wrap text-white/70">
      {steps.map((s, i) => (
        <span key={i} className="inline-flex items-center gap-2">
          <span className="kbd text-white/70 border border-white/15 px-3 py-1">{s}</span>
          {i < steps.length - 1 && <span className="text-[var(--gb-gold)] hidden md:inline">→</span>}
        </span>
      ))}
    </div>
  );
}

export default function ImpactCase() {
  const { slug } = useParams();
  const c = IMPACT_CASES[slug];
  if (!c) return <Navigate to="/impact" replace />;
  const next = c.next ? IMPACT_CASES[c.next] : null;

  return (
    <main data-testid="impact-case-page">
      {/* HERO */}
      <section className="relative min-h-[92vh] w-full overflow-hidden noise flex items-end bg-black">
        <div className="absolute inset-0 bg-cover bg-center opacity-55"
          style={{ backgroundImage: `url(${c.cover})` }} />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/60 to-black" />
        <div className="relative z-10 max-w-[1600px] mx-auto w-full px-6 md:px-12 lg:px-16 pt-32 pb-20 grid grid-cols-1 md:grid-cols-12 gap-8">
          <div className="md:col-span-12">
            <Link to="/impact" className="kbd text-white/50 hover:text-[var(--gb-gold)] inline-flex items-center gap-2 mb-10">
              <ArrowLeft size={12} /> Good Bandit / Impact
            </Link>
            <motion.p {...reveal} className="overline mb-8">{c.heroKicker}</motion.p>
            <motion.h1 {...reveal} className="font-display text-6xl md:text-8xl lg:text-9xl text-white leading-[0.9]">
              {c.heroTitle.map((line, i) => (
                <span key={i} className={`block ${i === c.heroTitle.length - 1 ? "italic text-[var(--gb-gold)]" : ""}`}>{line}</span>
              ))}
            </motion.h1>
            {c.heroTagline && (
              <motion.p {...reveal} className="font-display text-3xl md:text-5xl text-white/85 leading-[1.05] mt-8 max-w-3xl">
                {c.heroTagline.map((l, i) => <span key={i} className="block">{l}</span>)}
              </motion.p>
            )}
          </div>
          {c.heroStats && (
            <motion.div {...reveal} className="md:col-span-12 mt-10">
              <StatsBlock items={c.heroStats} size="hero" />
              {c.heroNote && <p className="kbd text-white/40 mt-8">{c.heroNote}</p>}
            </motion.div>
          )}
        </div>
      </section>

      {/* SECTIONS */}
      {c.sections.map((s, idx) => {
        if (s.type === "chapter") {
          return (
            <section key={idx} className="border-t border-white/5 bg-black" data-testid={`case-section-${idx}`}>
              <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 py-24 md:py-32 grid grid-cols-1 md:grid-cols-12 gap-10">
                <motion.div {...reveal} className="md:col-span-4">
                  <p className="kbd text-[var(--gb-gold)]">{s.n} / {s.t.toUpperCase()}</p>
                </motion.div>
                <motion.div {...reveal} className="md:col-span-8">
                  <h2 className="font-display text-4xl md:text-6xl text-white leading-[0.98]">
                    {s.h.map((l, i) => <span key={i} className={`block ${i === s.h.length - 1 ? "italic text-[var(--gb-gold)]" : ""}`}>{l}</span>)}
                  </h2>
                  {s.body && <p className="text-white/65 leading-relaxed mt-8 max-w-2xl">{s.body}</p>}
                  {s.stats && <div className="mt-10"><StatsBlock items={s.stats} size="chapter" /></div>}
                  {s.flow && <FlowLine steps={s.flow} />}
                  {s.gallery && (
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-10">
                      {s.gallery.map((g, i) => (
                        <div key={i} className="aspect-[4/5] bg-cover bg-center" style={{ backgroundImage: `url(${g})` }} />
                      ))}
                    </div>
                  )}
                </motion.div>
              </div>
            </section>
          );
        }
        if (s.type === "result") {
          return (
            <section key={idx} className="border-t border-white/5 bg-black" data-testid={`case-result-${idx}`}>
              <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 py-24 md:py-32">
                <motion.p {...reveal} className="overline mb-3">Result</motion.p>
                <motion.h2 {...reveal} className="font-display text-4xl md:text-6xl text-white leading-[0.95] mb-12">
                  {s.h.map((l, i) => <span key={i} className={`block ${i === s.h.length - 1 ? "italic text-[var(--gb-gold)]" : ""}`}>{l}</span>)}
                </motion.h2>
                <motion.div {...reveal}>
                  <StatsBlock items={s.stats} size="hero" />
                  {s.note && <p className="kbd text-white/40 mt-8 max-w-xl leading-relaxed">{s.note}</p>}
                </motion.div>
              </div>
            </section>
          );
        }
        if (s.type === "naming") {
          return (
            <section key={idx} className="border-t border-white/5 bg-black text-center" data-testid={`case-naming-${idx}`}>
              <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 py-32 md:py-40">
                <motion.p {...reveal} className="overline mb-8">Section 03 / Naming</motion.p>
                <motion.h2 {...reveal} className="font-display text-5xl md:text-8xl text-white leading-[0.95]">
                  {s.h}
                </motion.h2>
                <motion.p {...reveal} className="kbd text-white/50 my-10">{s.sub}</motion.p>
                <motion.p {...reveal} className="font-display text-6xl md:text-9xl italic text-[var(--gb-gold)] leading-[0.9]">
                  {s.finale}
                </motion.p>
              </div>
            </section>
          );
        }
        return null;
      })}

      {/* CAPABILITIES */}
      <section className="border-t border-white/5 bg-black">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 py-20">
          <motion.p {...reveal} className="overline mb-6">Capabilities</motion.p>
          <div className="flex flex-wrap gap-3">
            {c.capabilities.map((cap) => (
              <span key={cap} className="kbd border border-white/10 px-4 py-2 text-white/70">{cap}</span>
            ))}
          </div>
          {c.externalLink && (
            <a href={c.externalLink.url} target="_blank" rel="noopener noreferrer"
               className="gb-link inline-flex items-center gap-2 mt-10 text-[var(--gb-gold)] text-xs uppercase tracking-[0.25em]">
              {c.externalLink.label} <ExternalLink size={12} />
            </a>
          )}
        </div>
      </section>

      {/* NEXT + START A PROJECT */}
      <section className="border-t border-white/5 bg-black">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 py-24 grid grid-cols-1 md:grid-cols-2 gap-10">
          {next && (
            <Link to={`/impact/${next.slug}`} className="group block" data-testid="case-next">
              <p className="overline mb-4 text-white/50">Next project</p>
              <h3 className="font-display text-4xl md:text-5xl text-white leading-[1] group-hover:text-[var(--gb-gold)] transition-colors duration-500">
                {next.project} <ArrowRight className="inline-block ml-2 -mt-1" size={22} />
              </h3>
              <p className="kbd text-white/40 mt-4">{next.client} · {next.category}</p>
            </Link>
          )}
          <div className="md:text-right">
            <p className="overline mb-4">Work together</p>
            <h3 className="font-display text-4xl md:text-5xl text-white leading-[1]">
              Start a <span className="italic text-[var(--gb-gold)]">project.</span>
            </h3>
            <Link to="/contact" className="gb-btn-primary inline-flex mt-6" data-testid="case-start-cta">
              Start a project <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
